import React, { useEffect, useRef } from 'react';
import { ConditionalDisplay } from '@rsos/base-components';
import { AIR_AMBULANCE } from '@rsos/capstone/src/constants/capabilities';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import { captureException } from '@rsos/utils/sentry';
import styles from './LandingZonesEmbed.module.css';
import { templateHasAccountIdToken } from './buildLandingZonesUrl';
import useLandingZonesUrl from './useLandingZonesUrl';

/**
 * allow-scripts + allow-same-origin together only defeat the sandbox when the
 * framed document shares the framer's origin, letting it reach out and remove
 * its own sandbox attribute. The Landing Zone tool is third-party, so this is
 * safe — and an SSO'd map tool cannot function without scripts, forms, and
 * same-origin storage. Deliberately absent: allow-top-navigation (the frame
 * must not be able to navigate UNITE away) and allow-modals.
 */
const SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';

/**
 * One entry per thing that can be misconfigured about the URL itself.
 *
 * A single source of truth for two audiences: `reason` names the Hydra key or
 * the missing data field and goes in the hidden diagnostic; `message` is
 * generic copy for the admin actually looking at the page, who should not be
 * shown internal config keys.
 */
type ConfigRequirement = {
  ok: boolean;
  reason: string;
  message: string;
};

const LandingZonesEmbed: React.FC = () => {
  const isFlagEnabled = useBooleanFlagValue('manage_landing_zones', false);
  const { hasCapability } = useHasCapability(AIR_AMBULANCE);
  const { url, template, accountId } = useLandingZonesUrl();
  const hasReportedMissingToken = useRef(false);

  // A template with no {account_id} token loads fine but is unscoped, so it
  // fails silently in the field. Report it once per mount rather than guessing
  // a parameter name (see buildLandingZonesUrl).
  useEffect(() => {
    if (!template || hasReportedMissingToken.current) return;
    if (templateHasAccountIdToken(template)) return;

    hasReportedMissingToken.current = true;
    captureException(
      new Error(
        'GMR_LANDING_ZONE_ADMIN_URL is configured without the {account_id} token, ' +
          'so the embedded Landing Zone tool is not scoped to the authority',
      ),
    );
  }, [template]);

  const configRequirements: ConfigRequirement[] = [
    {
      ok: !!template,
      reason:
        'GMR_LANDING_ZONE_ADMIN_URL is not configured on portal_renderer in Hydra for this environment',
      message:
        'The Landing Zone management tool has not been set up for this environment yet.',
    },
    {
      ok: !!accountId,
      reason:
        "This ECC's authority record has no account_id, so the tool cannot be scoped to it",
      message:
        'This ECC is missing the account identifier the Landing Zone management tool needs.',
    },
  ];

  const unmetRequirements = configRequirements.filter(({ ok }) => !ok);

  // Flag and capability decide whether this page is meant for you at all — a
  // direct URL hit should not advertise anything, so those stay silent. A
  // misconfigured URL is different: an admin who got here legitimately deserves
  // to be told the page is unavailable rather than shown a blank panel. The
  // `&&` below is therefore a *visible* fallback, not a hidden gate, so it is
  // not the open-coded gating that AGENTS.md prohibits.
  const isPageForThisUser = !!isFlagEnabled && !!hasCapability;
  const showUnavailable = isPageForThisUser && unmetRequirements.length > 0;

  return (
    <>
      <ConditionalDisplay
        dataName="landing-zones-embed"
        conditions={[
          {
            showIf: !!isFlagEnabled,
            reason:
              'Needs the manage_landing_zones feature flag (set FEATURE_FLAGS.manage_landing_zones on portal_renderer in Hydra for this environment)',
          },
          {
            // hasCapability is typed boolean | undefined, so the coercion is
            // required rather than stylistic.
            showIf: !!hasCapability,
            reason:
              'ECC does not have the Air Ambulance capability (air_ambulance_0) enabled',
          },
          ...configRequirements.map(({ ok, reason }) => ({
            showIf: ok,
            reason,
          })),
        ]}
      >
        <div className={styles.container}>
          <iframe
            className={styles.frame}
            src={url}
            title="Landing Zones"
            sandbox={SANDBOX}
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
            data-name="landing-zones-iframe"
          />
        </div>
      </ConditionalDisplay>
      {showUnavailable && (
        <div className={styles.container}>
          <div
            className={styles.unavailable}
            data-name="landing-zones-unavailable"
          >
            {unmetRequirements.map(({ reason, message }) => (
              <span key={reason}>{message}</span>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default LandingZonesEmbed;
